import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
    Bot, Wand2, AlertCircle, Loader2, RefreshCw, Save, Phone, PhoneOff,
    History, MessageSquare, Sparkles, ChevronLeft, Send, Undo2, X,
    CheckCircle2, AlertTriangle, Info, PlayCircle, FlaskConical, Clock, UploadCloud
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import {
    fetchAiAgents, fetchAiAgentDetail, fetchRetellVoices, updateAiAgent,
    fetchAgentVersions, rollbackAgentVersion,
    fetchAgentTestCalls, startAgentTestCall, stopAgentTestCall,
    analyzeAgentTestCall, generateAgentFix, chatWithAgentEditor
} from "../services/crmServices/aiAgentService";
import { fetchRetellPhoneNumbers } from "../services/crmServices/campaignService";
import { DiffView, ToolChangeCard, ProposalModal } from "../components/aiCaller/PromptDiff";

const LANGUAGES = [
    "en-US", "en-GB", "en-AU", "en-NZ", "en-IN",
    "de-DE", "es-ES", "es-419", "fr-FR", "it-IT", "pt-PT", "pt-BR",
    "nl-NL", "pl-PL", "ro-RO", "ru-RU", "tr-TR", "sv-SE", "da-DK", "no-NO", "fi-FI",
    "el-GR", "hu-HU", "cs-CZ", "sk-SK", "bg-BG", "hr-HR", "lt-LT", "lv-LV", "et-EE",
    "ja-JP", "ko-KR", "zh-CN", "hi-IN", "ar-SA", "he-IL", "th-TH", "vi-VN", "id-ID", "ms-MY",
    "multi"
];

const SEVERITY_STYLES = {
    high: "bg-red-100 text-red-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-blue-100 text-blue-700"
};

export function AiAgents() {
    const { user } = useAuth();
    const [agents, setAgents] = useState([]);
    const [loadingAgents, setLoadingAgents] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    const loadAgents = useCallback(async () => {
        setLoadingAgents(true);
        setError(null);
        try {
            const res = await fetchAiAgents();
            if (res?.status === 200 && Array.isArray(res.data)) {
                setAgents(res.data);
            } else {
                setError(res?.data?.message || "Failed to load agents");
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load agents");
        } finally {
            setLoadingAgents(false);
        }
    }, []);

    useEffect(() => { loadAgents(); }, [loadAgents]);

    if (user?.position?.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-12">
                <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                    <div className="p-3 bg-red-50 rounded-full w-fit mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-sm text-gray-500">Administrator privileges required.</p>
                </div>
            </div>
        );
    }

    if (selectedId) {
        return (
            <AgentEditor
                agentId={selectedId}
                onBack={() => { setSelectedId(null); loadAgents(); }}
            />
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4 max-w-7xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-sm">
                        <Bot className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 tracking-tight">AI Agents</h1>
                        <p className="text-xs text-gray-500">Your RetellAI voice agents — edit prompts, run test calls, let Claude analyze and fix them</p>
                    </div>
                </div>
                <Button size="sm" variant="outline" onClick={loadAgents} disabled={loadingAgents}>
                    {loadingAgents ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Refresh
                </Button>
            </div>

            {loadingAgents ? (
                <Card><CardContent className="p-12 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-violet-500 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">Loading agents from RetellAI...</p>
                </CardContent></Card>
            ) : error ? (
                <Card><CardContent className="p-12 text-center">
                    <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                    <p className="text-sm text-red-600">{error}</p>
                </CardContent></Card>
            ) : agents.length === 0 ? (
                <Card><CardContent className="p-12 text-center">
                    <Bot className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-700">No agents found</p>
                    <p className="text-xs text-gray-500 mt-1">Create an agent in the RetellAI dashboard first — it will show up here.</p>
                </CardContent></Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {agents.map((agent) => (
                        <button
                            key={agent.agentId}
                            onClick={() => setSelectedId(agent.agentId)}
                            className="text-left bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md hover:border-violet-200 transition-all"
                        >
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="p-1.5 bg-violet-50 rounded-lg shrink-0">
                                        <Bot className="h-4 w-4 text-violet-600" />
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900 truncate">{agent.agentName}</span>
                                </div>
                                <Badge variant="outline" className="text-[9px] h-4 px-1.5 shrink-0">{agent.engineType}</Badge>
                            </div>
                            <div className="text-[11px] text-gray-500 space-y-0.5">
                                <div>Voice: {agent.voiceId || "—"} · {agent.language || "—"}</div>
                                <div>
                                    {agent.campaigns.length > 0 ? (
                                        <span className="flex flex-wrap gap-1 mt-1.5">
                                            {agent.campaigns.slice(0, 3).map((c) => (
                                                <Badge
                                                    key={c._id}
                                                    className={`text-[9px] h-4 px-1.5 ${c.status === "running" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}
                                                >
                                                    {c.name}{c.status === "running" ? " (live)" : ""}
                                                </Badge>
                                            ))}
                                            {agent.campaigns.length > 3 && (
                                                <span className="text-[9px] text-gray-400">+{agent.campaigns.length - 3} more</span>
                                            )}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">Not used by any campaign</span>
                                    )}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Agent editor ───────────────────────────────────────────────────────────
function AgentEditor({ agentId, onBack }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [voices, setVoices] = useState([]);

    // Editable fields
    const [agentName, setAgentName] = useState("");
    const [voiceId, setVoiceId] = useState("");
    const [language, setLanguage] = useState("");
    const [prompt, setPrompt] = useState("");
    const [beginMessage, setBeginMessage] = useState("");
    const [statePrompts, setStatePrompts] = useState({}); // { stateName: prompt }
    const [pendingToolChanges, setPendingToolChanges] = useState([]); // applied on Save
    const [saveSource, setSaveSource] = useState("manual");
    const [saving, setSaving] = useState(false);

    const [tab, setTab] = useState("chat"); // chat | calls | history
    const [proposal, setProposal] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchAiAgentDetail(agentId);
            if (res?.status === 200 && res.data?.agentId) {
                const d = res.data;
                setDetail(d);
                setAgentName(d.agentName || "");
                setVoiceId(d.voiceId || "");
                setLanguage(d.language || "");
                setPrompt(d.prompt || "");
                setBeginMessage(d.beginMessage || "");
                const sp = {};
                (d.states || []).forEach((s) => { sp[s.name] = s.prompt || ""; });
                setStatePrompts(sp);
                setPendingToolChanges([]);
                setSaveSource("manual");
            } else {
                setError(res?.data?.message || "Failed to load agent");
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load agent");
        } finally {
            setLoading(false);
        }
    }, [agentId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetchRetellVoices()
            .then((res) => setVoices(Array.isArray(res?.data) ? res.data : []))
            .catch(() => {});
    }, []);

    const dirtyStates = detail
        ? (detail.states || []).filter((s) => statePrompts[s.name] !== undefined && statePrompts[s.name] !== (s.prompt || "")).map((s) => s.name)
        : [];

    const isDirty = detail && (
        agentName !== (detail.agentName || "") ||
        voiceId !== (detail.voiceId || "") ||
        language !== (detail.language || "") ||
        prompt !== (detail.prompt || "") ||
        beginMessage !== (detail.beginMessage || "") ||
        dirtyStates.length > 0 ||
        pendingToolChanges.length > 0
    );

    const hasUnpublishedDraft = !!detail && detail.isPublished === false;

    const handleSave = async (publish) => {
        // Only publishing goes live, so only warn about running campaigns then.
        if (publish && detail?.activeCampaigns?.length > 0) {
            const ok = window.confirm(
                `This agent is used by RUNNING campaign(s): ${detail.activeCampaigns.join(", ")}.\nPublishing applies to new calls immediately. Publish anyway?`
            );
            if (!ok) return;
        }
        setSaving(true);
        try {
            const res = await updateAiAgent(agentId, {
                agentName, voiceId, language, prompt, beginMessage, statePrompts,
                toolChanges: pendingToolChanges,
                source: saveSource,
                publish: !!publish
            });
            if (res?.status === 200 && res.data) {
                const d = res.data;
                if (d.updated || d.published) {
                    if (d.published) {
                        toast.success(`Published — changes are live${d.changes?.length ? ` (${d.changes.join(", ")})` : ""}`);
                    } else {
                        toast.success(`Draft saved${d.changes?.length ? ` (${d.changes.join(", ")})` : ""} — not live until you publish`);
                    }
                    setDetail(d.detail);
                    const sp = {};
                    (d.detail?.states || []).forEach((s) => { sp[s.name] = s.prompt || ""; });
                    setStatePrompts(sp);
                    setPendingToolChanges([]);
                    setSaveSource("manual");
                } else {
                    toast.info("No changes to save");
                }
            } else {
                toast.error(res?.data?.message || "Save failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const applyProposal = (changes, toolChanges, source) => {
        (changes || []).forEach((c) => {
            if (c.target === "general") setPrompt(c.proposedPrompt);
            else setStatePrompts((sp) => ({ ...sp, [c.target]: c.proposedPrompt }));
        });
        if (toolChanges?.length > 0) {
            // Replace any pending change for the same tool+scope, then append
            setPendingToolChanges((prev) => [
                ...prev.filter((p) => !toolChanges.some((tc) => tc.scope === p.scope && tc.tool?.name === p.tool?.name)),
                ...toolChanges
            ]);
        }
        setSaveSource(source);
        setProposal(null);
        toast.success("Applied to editor — review, then Save draft or Publish");
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-12 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-violet-500 mx-auto mb-2" />
                <p className="text-xs text-gray-500">Loading agent from RetellAI...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-4 max-w-7xl">
                <Button size="sm" variant="outline" onClick={onBack} className="mb-4">
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
                </Button>
                <Card><CardContent className="p-12 text-center">
                    <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                    <p className="text-sm text-red-600">{error}</p>
                </CardContent></Card>
            </div>
        );
    }

    const voiceOptions = voices.map((v) => ({
        label: `${v.voiceName}${v.accent ? ` (${v.accent})` : ""}${v.provider ? ` — ${v.provider}` : ""}`,
        value: v.voiceId
    }));
    if (voiceId && !voiceOptions.some((o) => o.value === voiceId)) {
        voiceOptions.unshift({ label: voiceId, value: voiceId });
    }

    const languageOptions = LANGUAGES.map((l) => ({ label: l, value: l }));
    if (language && !LANGUAGES.includes(language)) {
        languageOptions.unshift({ label: language, value: language });
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4 max-w-[1500px]">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Button size="sm" variant="outline" onClick={onBack}>
                        <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
                    </Button>
                    <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg shadow-sm shrink-0">
                        <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold text-gray-900 truncate">{detail.agentName || "(unnamed agent)"}</h1>
                        <p className="text-[11px] text-gray-500">
                            {detail.engineType}{detail.engineModel ? ` · ${detail.engineModel}` : ""} · {agentId}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {isDirty
                        ? <Badge className="bg-amber-100 text-amber-700 text-[10px] h-5">Unsaved changes</Badge>
                        : hasUnpublishedDraft
                            ? <Badge className="bg-orange-100 text-orange-700 text-[10px] h-5">Draft — not live</Badge>
                            : <Badge className="bg-emerald-100 text-emerald-700 text-[10px] h-5">Live</Badge>}
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSave(false)}
                        disabled={saving || !isDirty}
                        title="Save your edits without going live"
                    >
                        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                        Save draft
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => handleSave(true)}
                        disabled={saving || (!isDirty && !hasUnpublishedDraft)}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                        title="Save (if needed) and make these changes live for calls"
                    >
                        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5 mr-1.5" />}
                        Publish
                    </Button>
                </div>
            </div>

            {detail.activeCampaigns?.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-xs text-amber-800">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    This agent is used by running campaign(s): <strong>{detail.activeCampaigns.join(", ")}</strong> — saved changes apply to new calls immediately.
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Left: settings + prompt */}
                <div className="space-y-4">
                    <Card>
                        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Agent name</Label>
                                <Input value={agentName} onChange={(e) => setAgentName(e.target.value)} className="mt-1 h-9" />
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Voice</Label>
                                <Select
                                    options={voiceOptions}
                                    value={voiceId}
                                    onChange={setVoiceId}
                                    searchable
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Language</Label>
                                <Select
                                    options={languageOptions}
                                    value={language}
                                    onChange={setLanguage}
                                    searchable
                                    className="mt-1"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-4">
                            {detail.promptSupported ? (
                                <>
                                    {detail.beginMessageSupported && (
                                        <div className="mb-3">
                                            <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Begin message</Label>
                                            <Input
                                                value={beginMessage}
                                                onChange={(e) => setBeginMessage(e.target.value)}
                                                placeholder="First thing the agent says (empty = wait for the customer)"
                                                className="mt-1 h-9"
                                            />
                                        </div>
                                    )}
                                    <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                                        Prompt {detail.engineType === "conversation-flow" ? "(global prompt)" : "(general prompt)"}
                                    </Label>
                                    <textarea
                                        value={prompt}
                                        onChange={(e) => setPrompt(e.target.value)}
                                        rows={detail.states?.length > 0 ? 12 : 26}
                                        spellCheck={false}
                                        className="w-full mt-1 px-3 py-2.5 text-[13px] leading-relaxed font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y"
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">{prompt.length.toLocaleString()} characters</p>

                                    {(detail.generalTools?.length > 0 || pendingToolChanges.length > 0) && (
                                        <div className="mt-4">
                                            <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-2">
                                                Tools / custom functions
                                            </Label>
                                            <div className="space-y-1.5">
                                                {(detail.generalTools || []).map((t) => (
                                                    <div key={t.name} className="flex items-center gap-1.5 text-[11px] border border-gray-100 rounded-md px-2.5 py-1.5 bg-gray-50/50 flex-wrap">
                                                        <Badge className="bg-gray-200 text-gray-700 text-[9px] h-4 px-1.5">{t.type}</Badge>
                                                        <span className="font-mono font-medium text-gray-800">{t.name}</span>
                                                        {t.description && <span className="text-gray-500 truncate max-w-[280px]">{t.description}</span>}
                                                    </div>
                                                ))}
                                                {pendingToolChanges.map((tc, i) => (
                                                    <div key={`pending-${i}`} className="flex items-start gap-2">
                                                        <div className="flex-1"><ToolChangeCard tc={tc} /></div>
                                                        <button
                                                            className="text-gray-400 hover:text-red-500 mt-2"
                                                            title="Remove this pending change"
                                                            onClick={() => setPendingToolChanges((prev) => prev.filter((_, j) => j !== i))}
                                                        >
                                                            <X className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            {pendingToolChanges.length > 0 && (
                                                <p className="text-[10px] text-amber-600 mt-1.5">
                                                    Pending tool changes apply when you Save draft or Publish.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {detail.states?.length > 0 && (
                                        <div className="mt-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                                                    Conversation states ({detail.states.length})
                                                </Label>
                                                {detail.startingState && (
                                                    <span className="text-[10px] text-gray-400">starts at "{detail.startingState}"</span>
                                                )}
                                            </div>
                                            <div className="space-y-1.5">
                                                {detail.states.map((s) => {
                                                    const dirty = dirtyStates.includes(s.name);
                                                    return (
                                                        <details key={s.name} className="border border-gray-200 rounded-lg bg-gray-50/50 group">
                                                            <summary className="px-3 py-2 cursor-pointer text-xs flex items-center gap-2 select-none">
                                                                <span className={`font-medium ${dirty ? "text-amber-700" : "text-gray-800"}`}>{s.name}</span>
                                                                {s.name === detail.startingState && (
                                                                    <Badge className="bg-emerald-100 text-emerald-700 text-[9px] h-4 px-1.5">start</Badge>
                                                                )}
                                                                {dirty && <Badge className="bg-amber-100 text-amber-700 text-[9px] h-4 px-1.5">edited</Badge>}
                                                                <span className="ml-auto text-[10px] text-gray-400">
                                                                    {(statePrompts[s.name] ?? "").length.toLocaleString()} chars
                                                                    {s.toolCount > 0 ? ` · ${s.toolCount} tool(s)` : ""}
                                                                </span>
                                                            </summary>
                                                            <div className="px-3 pb-3">
                                                                <textarea
                                                                    value={statePrompts[s.name] ?? ""}
                                                                    onChange={(e) => setStatePrompts((sp) => ({ ...sp, [s.name]: e.target.value }))}
                                                                    rows={10}
                                                                    spellCheck={false}
                                                                    className="w-full px-3 py-2 text-[12px] leading-relaxed font-mono border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y"
                                                                />
                                                                {s.edges?.length > 0 && (
                                                                    <div className="mt-1.5 text-[10px] text-gray-500 space-y-0.5">
                                                                        <span className="font-semibold text-gray-400 uppercase tracking-wide">Transitions</span>
                                                                        {s.edges.map((e, i) => (
                                                                            <div key={i} className="flex gap-1">
                                                                                <span className="text-violet-500 shrink-0">→ {e.destination}</span>
                                                                                {e.description && <span className="text-gray-400 truncate">— {e.description}</span>}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {s.tools?.length > 0 && (
                                                                    <div className="mt-1.5 text-[10px] text-gray-500">
                                                                        <span className="font-semibold text-gray-400 uppercase tracking-wide">State tools</span>
                                                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                                                            {s.tools.map((t) => (
                                                                                <Badge key={t.name} className="bg-gray-200 text-gray-700 text-[9px] h-4 px-1.5 font-mono">
                                                                                    {t.name}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </details>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="p-6 text-center text-xs text-gray-500">
                                    <Info className="h-5 w-5 mx-auto mb-2 text-gray-400" />
                                    Prompt editing is not available for engine type "{detail.engineType}".
                                    Voice, language and name can still be changed above.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Right: tabs */}
                <div>
                    <div className="flex gap-1 mb-3">
                        <TabButton active={tab === "chat"} onClick={() => setTab("chat")} icon={Sparkles} label="AI Assistant" />
                        <TabButton active={tab === "calls"} onClick={() => setTab("calls")} icon={Phone} label="Test Calls" />
                        <TabButton active={tab === "history"} onClick={() => setTab("history")} icon={History} label="History" />
                    </div>
                    {tab === "chat" && (
                        <ChatPanel
                            agentId={agentId}
                            promptSupported={detail.promptSupported}
                            onProposal={(p) => setProposal({ ...p, source: "ai-chat" })}
                        />
                    )}
                    {tab === "calls" && (
                        <TestCallsPanel
                            agentId={agentId}
                            promptSupported={detail.promptSupported}
                            dynamicVariables={detail.dynamicVariables || []}
                            hasTransferTool={[
                                ...(detail.generalTools || []),
                                ...((detail.states || []).flatMap((s) => s.tools || []))
                            ].some((t) => t?.type === "transfer_call")}
                            onProposal={(p) => setProposal({ ...p, source: "ai-fix" })}
                        />
                    )}
                    {tab === "history" && (
                        <HistoryPanel agentId={agentId} onRolledBack={load} />
                    )}
                </div>
            </div>

            <ProposalModal
                proposal={proposal}
                agentId={agentId}
                onClose={() => setProposal(null)}
                onApply={(changes, toolChanges) => applyProposal(changes, toolChanges, proposal.source)}
            />
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                active ? "bg-violet-600 text-white shadow-sm" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}
        >
            <Icon className="h-3.5 w-3.5" />
            {label}
        </button>
    );
}

// ── Claude chat panel ──────────────────────────────────────────────────────
const chatStorageKey = (agentId) => `aiAgentChat:${agentId}`;

function ChatPanel({ agentId, promptSupported, onProposal }) {
    const [messages, setMessages] = useState(() => {
        try {
            const saved = localStorage.getItem(chatStorageKey(agentId));
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const bottomRef = useRef(null);

    // Reload the saved conversation when switching between agents
    useEffect(() => {
        try {
            const saved = localStorage.getItem(chatStorageKey(agentId));
            setMessages(saved ? JSON.parse(saved) : []);
        } catch {
            setMessages([]);
        }
    }, [agentId]);

    // Persist per-agent so a page refresh keeps the conversation
    useEffect(() => {
        try {
            if (messages.length > 0) {
                localStorage.setItem(chatStorageKey(agentId), JSON.stringify(messages));
            } else {
                localStorage.removeItem(chatStorageKey(agentId));
            }
        } catch { /* storage full or unavailable — ignore */ }
    }, [messages, agentId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const clearChat = () => {
        setMessages([]);
        try { localStorage.removeItem(chatStorageKey(agentId)); } catch { /* ignore */ }
    };

    const send = async () => {
        const content = input.trim();
        if (!content || busy) return;
        const next = [...messages, { role: "user", content }];
        setMessages(next);
        setInput("");
        setBusy(true);
        try {
            const res = await chatWithAgentEditor(agentId, next.map(({ role, content }) => ({ role, content })));
            if (res?.status === 200 && res.data) {
                setMessages([...next, {
                    role: "assistant",
                    content: res.data.reply || "(no reply)",
                    changes: res.data.changes || [],
                    toolChanges: res.data.toolChanges || []
                }]);
            } else {
                toast.error(res?.data?.message || "Claude request failed");
                setMessages(next);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Claude request failed");
            setMessages(next);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card>
            <CardContent className="p-0 flex flex-col" style={{ height: "620px" }}>
                {messages.length > 0 && (
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/60">
                        <span className="text-[11px] text-gray-500">
                            Conversation saved on this device — kept after refresh
                        </span>
                        <button
                            onClick={clearChat}
                            disabled={busy}
                            className="text-[11px] text-gray-500 hover:text-red-600 flex items-center gap-1"
                        >
                            <X className="h-3 w-3" /> Clear chat
                        </button>
                    </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.length === 0 && (
                        <div className="text-center py-10">
                            <Sparkles className="h-8 w-8 text-orange-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-gray-700">Ask Claude to improve this agent</p>
                            <div className="text-xs text-gray-500 mt-2 space-y-1 max-w-sm mx-auto">
                                <p>"Make the agent more persistent about booking a call"</p>
                                <p>"Add objection handling for people who lost money before"</p>
                                <p>"Why does my agent sound robotic? Look at the prompt"</p>
                            </div>
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-xs leading-relaxed whitespace-pre-wrap ${
                                m.role === "user" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-800"
                            }`}>
                                {m.content}
                                {(m.changes?.length > 0 || m.toolChanges?.length > 0) && (
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                        <Button
                                            size="sm"
                                            className="h-7 text-[11px] bg-orange-500 hover:bg-orange-600 text-white"
                                            onClick={() => onProposal({
                                                changes: m.changes || [],
                                                toolChanges: m.toolChanges || [],
                                                explanation: "Proposed by Claude in chat"
                                            })}
                                        >
                                            <Wand2 className="h-3 w-3 mr-1" />
                                            Review proposal
                                            {m.changes?.length > 0 ? ` — ${m.changes.length} prompt(s)` : ""}
                                            {m.toolChanges?.length > 0 ? ` — ${m.toolChanges.length} tool(s)` : ""}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex justify-start">
                            <div className="bg-gray-100 rounded-xl px-3.5 py-2.5">
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                            </div>
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>
                <div className="border-t border-gray-100 p-3 flex gap-2">
                    <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={promptSupported ? "Tell Claude what to change..." : "Prompt editing not supported for this agent"}
                        disabled={busy || !promptSupported}
                        className="h-9 text-xs"
                    />
                    <Button size="sm" onClick={send} disabled={busy || !input.trim() || !promptSupported} className="h-9 bg-violet-600 hover:bg-violet-700 text-white">
                        <Send className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

// ── Test calls panel ───────────────────────────────────────────────────────
function TestCallsPanel({ agentId, promptSupported, dynamicVariables = [], hasTransferTool = false, onProposal }) {
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [phone, setPhone] = useState(() => localStorage.getItem("aiAgentTestPhone") || "");
    const [customerEmail, setCustomerEmail] = useState(() => localStorage.getItem("aiAgentTestEmail") || "");
    const [transferNumber, setTransferNumber] = useState(() => localStorage.getItem("aiAgentTestTransferNumber") || "");
    const [fromNumbers, setFromNumbers] = useState([]);
    const [fromNumber, setFromNumber] = useState(() => localStorage.getItem("aiAgentTestFromNumber") || "");
    const [varValues, setVarValues] = useState({});
    const [starting, setStarting] = useState(false);

    // Restore per-agent saved variable values
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(`aiAgentTestVars:${agentId}`) || "{}");
            setVarValues(saved && typeof saved === "object" ? saved : {});
        } catch {
            setVarValues({});
        }
    }, [agentId]);
    const [busyId, setBusyId] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [selectedFindings, setSelectedFindings] = useState({});
    const pollRef = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetchAgentTestCalls(agentId);
            if (res?.status === 200 && Array.isArray(res.data)) setCalls(res.data);
        } catch { /* silent */ }
        finally { if (!silent) setLoading(false); }
    }, [agentId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetchRetellPhoneNumbers()
            .then((res) => {
                const nums = Array.isArray(res?.data) ? res.data : [];
                setFromNumbers(nums);
                // Preselect: saved choice if still valid, otherwise the first number
                setFromNumber((prev) =>
                    prev && nums.some((n) => n.phone_number === prev)
                        ? prev
                        : (nums[0]?.phone_number || "")
                );
            })
            .catch(() => {});
    }, []);

    // Poll while a call is in progress
    useEffect(() => {
        const active = calls.some((c) => ["registered", "ongoing"].includes(c.status)) ||
            calls.some((c) => c.status === "ended" && !c.transcript);
        clearInterval(pollRef.current);
        if (active) {
            pollRef.current = setInterval(() => load(true), 5000);
        }
        return () => clearInterval(pollRef.current);
    }, [calls, load]);

    const start = async () => {
        if (!phone.trim()) { toast.error("Enter your phone number"); return; }
        const emailTrim = customerEmail.trim();
        if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
            toast.error("Enter a valid customer email (or leave it blank)");
            return;
        }
        localStorage.setItem("aiAgentTestPhone", phone.trim());
        localStorage.setItem("aiAgentTestEmail", emailTrim);
        if (fromNumber) localStorage.setItem("aiAgentTestFromNumber", fromNumber);
        localStorage.setItem("aiAgentTestTransferNumber", transferNumber.trim());
        try { localStorage.setItem(`aiAgentTestVars:${agentId}`, JSON.stringify(varValues)); } catch { /* ignore */ }
        setStarting(true);
        // The transfer tool dials {{transfer_number}} — inject it so live transfers
        // can be tested from here (it isn't a prompt placeholder, so it's not auto-detected).
        const varsToSend = { ...varValues };
        if (hasTransferTool && transferNumber.trim()) varsToSend.transfer_number = transferNumber.trim();
        try {
            const res = await startAgentTestCall(agentId, phone.trim(), fromNumber || undefined, varsToSend, emailTrim || undefined);
            if (res?.status === 200 && res.data?._id) {
                toast.success("Calling you now...");
                load(true);
            } else {
                toast.error(res?.data?.message || "Failed to start call");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to start call");
        } finally {
            setStarting(false);
        }
    };

    const stop = async (call) => {
        setBusyId(call._id);
        try {
            await stopAgentTestCall(call._id);
            // Optimistically flip to ended so the Hang up button disappears now
            setCalls((prev) => prev.map((c) => (c._id === call._id ? { ...c, status: "ended" } : c)));
            toast.success("Call stopped");
            setTimeout(() => load(true), 1500);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to stop call");
        } finally {
            setBusyId(null);
        }
    };

    const analyze = async (call) => {
        setBusyId(call._id);
        try {
            const res = await analyzeAgentTestCall(call._id);
            if (res?.status === 200 && res.data?.aiAnalysis) {
                toast.success("Analysis complete");
                setExpanded(call._id);
                setSelectedFindings({});
                load(true);
            } else {
                toast.error(res?.data?.message || "Analysis failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Analysis failed");
        } finally {
            setBusyId(null);
        }
    };

    const fix = async (call) => {
        const indexes = Object.entries(selectedFindings)
            .filter(([, v]) => v)
            .map(([k]) => parseInt(k, 10));
        setBusyId(call._id);
        try {
            const res = await generateAgentFix(call._id, indexes.length > 0 ? indexes : undefined);
            if (res?.status === 200 && (res.data?.changes?.length > 0 || res.data?.toolChanges?.length > 0)) {
                onProposal(res.data);
            } else {
                toast.error(res?.data?.message || "Fix generation failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Fix generation failed");
        } finally {
            setBusyId(null);
        }
    };

    const statusBadge = (c) => {
        const map = {
            registered: ["bg-blue-100 text-blue-700", "Ringing"],
            ongoing: ["bg-emerald-100 text-emerald-700 animate-pulse", "Live"],
            ended: ["bg-gray-100 text-gray-600", "Ended"],
            analyzed: ["bg-violet-100 text-violet-700", "Analyzed"],
            error: ["bg-red-100 text-red-700", "Error"]
        };
        const [cls, label] = map[c.status] || map.ended;
        return <Badge className={`${cls} text-[10px] h-5`}>{label}</Badge>;
    };

    return (
        <Card>
            <CardContent className="p-4 space-y-3" style={{ minHeight: "620px" }}>
                {/* New call */}
                <div className="flex flex-wrap gap-2 items-end p-3 bg-violet-50/50 border border-violet-100 rounded-lg">
                    <div className="flex-1 min-w-[150px]">
                        <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Call me at</Label>
                        <Input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+352 691 234 567"
                            className="mt-1 h-9 bg-white"
                        />
                    </div>
                    <div className="flex-1 min-w-[170px]">
                        <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Call from</Label>
                        <select
                            value={fromNumber}
                            onChange={(e) => setFromNumber(e.target.value)}
                            className="mt-1 h-9 w-full px-2 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                        >
                            {fromNumbers.length === 0 && <option value="">Default number (Settings)</option>}
                            {fromNumbers.map((n) => (
                                <option key={n.phone_number} value={n.phone_number}>
                                    {n.nickname ? `${n.nickname} — ` : ""}{n.phone_number_pretty || n.phone_number}
                                </option>
                            ))}
                        </select>
                    </div>
                    <Button size="sm" onClick={start} disabled={starting} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white">
                        {starting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Phone className="h-3.5 w-3.5 mr-1.5" />}
                        Test Call
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => load()} disabled={loading} className="h-9">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>

                    {/* Customer email — recipient used if the agent sends an email during the test */}
                    <div className="w-full mt-1">
                        <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Customer email (optional)</Label>
                        <Input
                            type="email"
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            placeholder="you@example.com — where test emails are sent if the agent emails during the call"
                            className="mt-1 h-9 bg-white"
                        />
                        <p className="text-[10px] text-gray-400 mt-1">
                            If the agent uses its send_email tool during this test call, the email goes here (there's no real lead on a test call).
                        </p>
                    </div>

                    {/* Transfer number — only for agents that have a transfer tool */}
                    {hasTransferTool && (
                        <div className="w-full mt-1">
                            <Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Transfer to number (optional)</Label>
                            <Input
                                type="tel"
                                value={transferNumber}
                                onChange={(e) => setTransferNumber(e.target.value)}
                                placeholder="+441234567890 — where the agent transfers the call if you ask for an expert"
                                className="mt-1 h-9 bg-white"
                            />
                            <p className="text-[10px] text-gray-400 mt-1">
                                Sent to the agent as <span className="font-mono">{`{{transfer_number}}`}</span>. If you ask to be connected to an expert during the test, the call is really transferred to this number.
                            </p>
                        </div>
                    )}

                    {/* Dynamic variables the agent's prompt expects (e.g. customer_name) */}
                    {dynamicVariables.length > 0 && (
                        <div className="w-full mt-1">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                Dynamic variables — values the agent uses in this call
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {dynamicVariables.map((name) => (
                                    <div key={name}>
                                        <Label className="text-[10px] text-gray-500 font-mono">{`{{${name}}}`}</Label>
                                        <Input
                                            value={varValues[name] || ""}
                                            onChange={(e) => setVarValues((v) => ({ ...v, [name]: e.target.value }))}
                                            placeholder={name}
                                            className="mt-0.5 h-8 text-xs bg-white"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Calls list */}
                {loading ? (
                    <div className="text-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-violet-500 mx-auto" />
                    </div>
                ) : calls.length === 0 ? (
                    <div className="text-center py-10 text-xs text-gray-500">
                        <FlaskConical className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                        No test calls yet. Enter your number and hit Test Call — then analyze it with Claude.
                    </div>
                ) : (
                    <div className="space-y-2 overflow-y-auto" style={{ maxHeight: "520px" }}>
                        {calls.map((c) => {
                            const isExpanded = expanded === c._id;
                            const canAnalyze = ["ended", "analyzed"].includes(c.status) && c.transcript;
                            return (
                                <div key={c._id} className="border border-gray-100 rounded-lg overflow-hidden">
                                    <div
                                        className="flex items-center gap-2 px-3 py-2.5 bg-white hover:bg-gray-50 cursor-pointer"
                                        onClick={() => setExpanded(isExpanded ? null : c._id)}
                                    >
                                        {statusBadge(c)}
                                        <span className="text-xs text-gray-700 font-mono">{c.toNumber}</span>
                                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            {new Date(c.createdAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                            {c.durationSeconds ? ` · ${c.durationSeconds}s` : ""}
                                        </span>
                                        <span className="ml-auto flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                                            {["registered", "ongoing"].includes(c.status) && (
                                                <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600" onClick={() => stop(c)} disabled={busyId === c._id}>
                                                    <PhoneOff className="h-3 w-3 mr-1" /> Hang up
                                                </Button>
                                            )}
                                            {canAnalyze && !c.aiAnalysis?.analyzedAt && (
                                                <Button size="sm" className="h-7 text-[11px] bg-orange-500 hover:bg-orange-600 text-white" onClick={() => analyze(c)} disabled={busyId === c._id}>
                                                    {busyId === c._id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                                                    Analyze
                                                </Button>
                                            )}
                                        </span>
                                    </div>

                                    {isExpanded && (
                                        <div className="px-3 pb-3 pt-1 bg-gray-50/50 space-y-3">
                                            {c.fromNumber && (
                                                <p className="text-[10px] text-gray-400">Called from {c.fromNumber}</p>
                                            )}
                                            {c.customerEmail && (
                                                <p className="text-[10px] text-gray-400">Emails to {c.customerEmail}</p>
                                            )}
                                            {c.dynamicVariables && Object.keys(c.dynamicVariables).length > 0 && (
                                                <p className="text-[10px] text-gray-400">
                                                    Variables: {Object.entries(c.dynamicVariables).map(([k, v]) => `${k}=${v}`).join(", ")}
                                                </p>
                                            )}
                                            {c.error && <p className="text-xs text-red-600">{c.error}</p>}
                                            {c.recordingUrl && (
                                                <audio controls src={c.recordingUrl} className="w-full h-9" preload="none" />
                                            )}
                                            {c.callSummary && (
                                                <p className="text-[11px] text-gray-600 bg-white border border-gray-100 rounded-md p-2">
                                                    <strong>Retell summary:</strong> {c.callSummary}
                                                </p>
                                            )}
                                            {c.transcript && (
                                                <details className="bg-white border border-gray-100 rounded-md">
                                                    <summary className="text-[11px] font-semibold text-gray-600 px-2 py-1.5 cursor-pointer">Transcript</summary>
                                                    <pre className="text-[11px] text-gray-700 whitespace-pre-wrap p-2 max-h-48 overflow-y-auto m-0">{c.transcript}</pre>
                                                </details>
                                            )}

                                            {/* Claude analysis */}
                                            {c.aiAnalysis?.analyzedAt && (
                                                <div className="bg-white border border-orange-100 rounded-lg p-3 space-y-2">
                                                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-orange-700">
                                                        <Sparkles className="h-3.5 w-3.5" /> Claude analysis
                                                    </div>
                                                    <p className="text-xs text-gray-700">{c.aiAnalysis.summary}</p>
                                                    {c.aiAnalysis.whatWorked?.length > 0 && (
                                                        <div className="text-[11px] text-emerald-700">
                                                            {c.aiAnalysis.whatWorked.map((w, i) => (
                                                                <div key={i} className="flex gap-1.5"><CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" /> {w}</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {c.aiAnalysis.findings?.length > 0 ? (
                                                        <>
                                                            <div className="space-y-1.5">
                                                                {c.aiAnalysis.findings.map((f, i) => (
                                                                    <label key={i} className="flex gap-2 p-2 bg-gray-50 rounded-md cursor-pointer border border-transparent hover:border-orange-200">
                                                                        <Checkbox
                                                                            checked={selectedFindings[i] ?? true}
                                                                            onCheckedChange={(v) => setSelectedFindings((s) => ({ ...s, [i]: !!v }))}
                                                                            className="mt-0.5"
                                                                        />
                                                                        <div className="text-[11px] min-w-0">
                                                                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                                                                <Badge className={`${SEVERITY_STYLES[f.severity]} text-[9px] h-4 px-1`}>{f.severity}</Badge>
                                                                                {f.targetState && (
                                                                                    <Badge className={`text-[9px] h-4 px-1 ${f.targetState === "general" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
                                                                                        {f.targetState === "general" ? "general prompt" : f.targetState}
                                                                                    </Badge>
                                                                                )}
                                                                                <span className="font-medium text-gray-800">{f.problem}</span>
                                                                            </div>
                                                                            {f.transcriptMoment && <p className="text-gray-500 italic">"{f.transcriptMoment}"</p>}
                                                                            <p className="text-gray-600 mt-0.5"><strong>Cause:</strong> {f.promptCause}</p>
                                                                            <p className="text-emerald-700 mt-0.5"><strong>Fix:</strong> {f.suggestedFix}</p>
                                                                        </div>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            {promptSupported && (
                                                                <Button
                                                                    size="sm"
                                                                    className="w-full h-8 text-[11px] bg-orange-500 hover:bg-orange-600 text-white"
                                                                    onClick={() => fix(c)}
                                                                    disabled={busyId === c._id}
                                                                >
                                                                    {busyId === c._id ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Wand2 className="h-3 w-3 mr-1.5" />}
                                                                    Fix selected findings — generate revised prompt
                                                                </Button>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <p className="text-[11px] text-emerald-600">No problems found — this call went well.</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ── Version history panel ──────────────────────────────────────────────────
function HistoryPanel({ agentId, onRolledBack }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [diffVersion, setDiffVersion] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAgentVersions(agentId);
            if (res?.status === 200 && Array.isArray(res.data)) setVersions(res.data);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, [agentId]);

    useEffect(() => { load(); }, [load]);

    const rollback = async (v) => {
        if (!window.confirm("Restore the agent to the state BEFORE this change? This publishes to RetellAI immediately.")) return;
        setBusyId(v._id);
        try {
            const res = await rollbackAgentVersion(agentId, v._id);
            if (res?.status === 200 && res.data?.updated !== undefined) {
                toast.success("Rolled back and published to RetellAI");
                load();
                onRolledBack();
            } else {
                toast.error(res?.data?.message || "Rollback failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Rollback failed");
        } finally {
            setBusyId(null);
        }
    };

    const SOURCE_LABELS = {
        manual: ["bg-gray-100 text-gray-600", "Manual"],
        "ai-chat": ["bg-orange-100 text-orange-700", "AI Chat"],
        "ai-fix": ["bg-violet-100 text-violet-700", "AI Fix"],
        rollback: ["bg-blue-100 text-blue-700", "Rollback"]
    };

    // All prompt diffs in a version: general + any changed state prompts
    const versionDiffs = (v) => {
        const diffs = [];
        if ((v.before?.prompt || "") !== (v.after?.prompt || "")) {
            diffs.push({ target: "general", oldText: v.before?.prompt || "", newText: v.after?.prompt || "" });
        }
        const beforeStates = {};
        (v.before?.statePrompts || []).forEach((s) => { beforeStates[s.name] = s.prompt || ""; });
        (v.after?.statePrompts || []).forEach((s) => {
            if ((beforeStates[s.name] ?? "") !== (s.prompt || "")) {
                diffs.push({ target: s.name, oldText: beforeStates[s.name] ?? "", newText: s.prompt || "" });
            }
        });
        return diffs;
    };

    return (
        <Card>
            <CardContent className="p-4" style={{ minHeight: "620px" }}>
                {loading ? (
                    <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin text-violet-500 mx-auto" /></div>
                ) : versions.length === 0 ? (
                    <div className="text-center py-10 text-xs text-gray-500">
                        <History className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                        No changes recorded yet. Every save from this page is snapshotted here.
                    </div>
                ) : (
                    <div className="space-y-2 overflow-y-auto" style={{ maxHeight: "580px" }}>
                        {versions.map((v) => {
                            const [cls, label] = SOURCE_LABELS[v.source] || SOURCE_LABELS.manual;
                            return (
                                <div key={v._id} className="border border-gray-100 rounded-lg px-3 py-2.5 bg-white">
                                    <div className="flex items-center gap-2">
                                        <Badge className={`${cls} text-[10px] h-5`}>{label}</Badge>
                                        <span className="text-xs text-gray-700">{v.changeSummary}</span>
                                        <span className="ml-auto text-[10px] text-gray-400">
                                            {v.changedBy ? `${v.changedBy.firstName || ""} ${v.changedBy.lastName || ""}`.trim() : "—"}
                                            {" · "}
                                            {new Date(v.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                        </span>
                                    </div>
                                    <div className="flex gap-2 mt-1.5">
                                        {versionDiffs(v).length > 0 && (
                                            <button
                                                className="text-[11px] text-blue-600 hover:underline"
                                                onClick={() => setDiffVersion(v)}
                                            >
                                                View prompt diff{versionDiffs(v).length > 1 ? `s (${versionDiffs(v).length})` : ""}
                                            </button>
                                        )}
                                        <button
                                            className="text-[11px] text-gray-500 hover:text-red-600 hover:underline flex items-center gap-0.5"
                                            onClick={() => rollback(v)}
                                            disabled={busyId === v._id}
                                        >
                                            <Undo2 className="h-3 w-3" /> Restore state before this change
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {diffVersion && (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDiffVersion(null)}>
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                                <h3 className="text-sm font-semibold text-gray-900">Prompt change — {new Date(diffVersion.createdAt).toLocaleString()}</h3>
                                <button onClick={() => setDiffVersion(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                            </div>
                            <div className="p-5 overflow-y-auto flex-1 space-y-4">
                                {versionDiffs(diffVersion).map((d) => (
                                    <div key={d.target}>
                                        <Badge className={`mb-1.5 text-[10px] h-5 ${d.target === "general" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
                                            {d.target === "general" ? "General prompt" : `State: ${d.target}`}
                                        </Badge>
                                        <DiffView oldText={d.oldText} newText={d.newText} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
